import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";

/**
 * Self-playing, looping demo: CPT codes auto-populate a billing ledger while a
 * Care Plan card fills with suggested goals/exercises — Notation's UI language,
 * no user interaction.
 */

interface Row {
  section: "Evaluations" | "Therapeutic Procedures";
  code: string;
  tag: "Timed" | "Untimed";
  desc: string;
  mods: string[];
  units: number | null;
}

const ROWS: Row[] = [
  {
    section: "Evaluations",
    code: "97162",
    tag: "Untimed",
    desc: "PT Eval – Moderate Complexity",
    mods: ["GP"],
    units: null,
  },
  {
    section: "Therapeutic Procedures",
    code: "97110",
    tag: "Timed",
    desc: "Therapeutic Exercise",
    mods: ["GP", "59"],
    units: 1,
  },
  {
    section: "Therapeutic Procedures",
    code: "97140",
    tag: "Timed",
    desc: "Manual Therapy",
    mods: ["GP"],
    units: 1,
  },
  {
    section: "Therapeutic Procedures",
    code: "97530",
    tag: "Timed",
    desc: "Therapeutic Activities",
    mods: ["GP"],
    units: 1,
  },
];

const SECTIONS: Row["section"][] = ["Evaluations", "Therapeutic Procedures"];

const CARE_PLAN = [
  "Therapeutic Exercise",
  "Manual Therapy",
  "Pendulum Exercise",
  "Wall Slide",
  "HEP Handout",
];

/** Cumulative per frame: rows shown, rows confirmed (mods+units+check), plan chips lit. */
const FRAMES: { r: number; cf: number; p: number }[] = [
  { r: 1, cf: 0, p: 0 },
  { r: 1, cf: 1, p: 1 },
  { r: 2, cf: 1, p: 2 },
  { r: 2, cf: 2, p: 2 },
  { r: 3, cf: 2, p: 3 },
  { r: 3, cf: 3, p: 4 },
  { r: 4, cf: 3, p: 4 },
  { r: 4, cf: 4, p: 5 },
];

const STEP_MS = 950;
const HOLD_MS = 2400;

export default function BillingCodesWidget() {
  const [frame, setFrame] = useState(0);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce) {
      setFrame(FRAMES.length - 1);
      return;
    }

    const isLast = frame >= FRAMES.length - 1;
    timeoutRef.current = window.setTimeout(
      () => setFrame((f) => (f >= FRAMES.length - 1 ? 0 : f + 1)),
      isLast ? HOLD_MS : STEP_MS
    );
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [frame]);

  const { r, cf, p } = FRAMES[frame];
  const visibleRows = ROWS.slice(0, r);

  return (
    <div className="relative w-full h-full font-productSans">
      {/* Billing ledger card */}
      <div className="absolute left-0 top-[4%] bottom-[4%] w-[72%] rounded-[24px] bg-white shadow-[0_20px_56px_-18px_rgba(31,61,59,0.42)] overflow-hidden flex flex-col z-10">
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#1F3D3B]">
          <span className="text-[12px] font-semibold text-white">Billing Codes</span>
          <span className="flex items-center gap-1.5 text-[9px] font-medium text-white/80">
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-[#7DD3FC]"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            />
            Auto-coding
          </span>
        </div>

        <div className="flex-1 min-h-0 px-3 py-3 flex flex-col gap-3 overflow-hidden">
          {SECTIONS.map((section) => {
            const sectionRows = visibleRows.filter((row) => row.section === section);
            if (sectionRows.length === 0) return null;
            return (
              <div key={section}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10.5px] font-semibold text-gray-800">
                    {section}
                  </span>
                  <span className="text-[9px] text-gray-400">
                    {sectionRows.length} code{sectionRows.length > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <AnimatePresence initial={false}>
                    {sectionRows.map((row) => {
                      const globalIndex = ROWS.indexOf(row);
                      const confirmed = globalIndex < cf;
                      return (
                        <motion.div
                          key={row.code}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                          className="rounded-xl bg-gray-50 px-2.5 py-2"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-bold text-gray-900">
                              {row.code}
                            </span>
                            <span
                              className={`text-[8.5px] font-medium px-1.5 py-0.5 rounded-full ${
                                row.tag === "Timed"
                                  ? "bg-[#eef4f1] text-[#495B25]"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {row.tag}
                            </span>
                            <AnimatePresence>
                              {confirmed && (
                                <motion.span
                                  initial={{ scale: 0, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  className="ml-auto flex items-center justify-center w-4 h-4 rounded-full bg-[#486633]"
                                >
                                  <Check className="w-2.5 h-2.5 text-white" />
                                </motion.span>
                              )}
                            </AnimatePresence>
                          </div>
                          <p className="text-[10px] text-gray-500 mt-0.5">{row.desc}</p>
                          <AnimatePresence>
                            {confirmed && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                className="flex items-center gap-1.5 mt-1.5 overflow-hidden"
                              >
                                {row.mods.map((mod) => (
                                  <span
                                    key={mod}
                                    className="text-[8.5px] font-medium px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-600"
                                  >
                                    {mod}
                                  </span>
                                ))}
                                {row.units != null && (
                                  <span className="text-[9px] text-gray-500 ml-auto">
                                    {row.units} unit
                                  </span>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Care Plan card — overlaps top-right */}
      <div className="absolute right-0 top-[14%] w-[42%] rounded-[24px] bg-white shadow-[0_24px_64px_-18px_rgba(31,61,59,0.45)] overflow-hidden z-20">
        <div className="px-3.5 pt-3.5 pb-2">
          <p className="text-[12px] font-semibold text-[#1F3D3B] mb-2">Care Plan</p>
          <div className="flex flex-col gap-1.5">
            {CARE_PLAN.map((item, i) => {
              const lit = i < p;
              return (
                <motion.div
                  key={item}
                  animate={{ opacity: lit ? 1 : 0.4 }}
                  transition={{ duration: 0.3 }}
                  className="flex items-center gap-2"
                >
                  <span
                    className={`flex items-center justify-center w-3.5 h-3.5 rounded-full shrink-0 transition-colors duration-300 ${
                      lit ? "bg-[#486633]" : "bg-gray-200"
                    }`}
                  >
                    <Check
                      className={`w-2 h-2 ${lit ? "text-white" : "text-gray-400"}`}
                    />
                  </span>
                  <span
                    className={`text-[10px] ${
                      lit ? "text-gray-700 font-medium" : "text-gray-400"
                    }`}
                  >
                    {item}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
